/* ============================================================================
   M7 — Courier Overcharge Errors  (PREVIEW · Expected next sprint)
   ----------------------------------------------------------------------------
   Detects when a courier partner bills Prozo above the agreed rate card for the
   same shipment. Each AWB on the courier invoice is matched to the governing
   ProShip rate card for that courier code, zone and weight slab; any variance
   above the ₹5 tolerance is flagged as an overcharge error so the commercial
   team can raise a structured dispute with the courier.

   This page is intentionally an "under development" hero state, but it ships a
   fully interactive DEMO so reviewers can see the intended output: upload an
   AWB-level invoice (or click the demo button) and a sample variance table is
   rendered from dummy rows. Nothing is persisted — clearly marked as a preview.
   React port of the mockup js/m7.js.
   ========================================================================== */
import React, {useState} from 'react'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined'
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'

import PageTitle from '../../../components/PageTitle'
import {PageHeader, Badge, KpiStrip, FileDrop, SupplyTable, useToast, inr} from '../components'
import {DEMO_ROWS, TOL, TEMPLATE_NAME, TEMPLATE_CONTENT, variance, isError} from './overchargeData'

/* "How it will work" timeline steps (per mockup) */
const HOW_STEPS = [
  [
    '1',
    'Courier uploads an AWB-level invoice',
    'The courier (or our team) uploads the monthly invoice as Excel/CSV — one row per AWB with the billed amount, courier code, zone and billed weight.',
  ],
  [
    '2',
    'System matches each AWB to the ProShip rate card',
    'For every AWB, the engine looks up the governing rate card for that courier code, zone and weight slab — using the currently signed agreement, not a pending addendum.',
  ],
  [
    '3',
    `Variances above ${inr(TOL)} are flagged as errors`,
    `Billed − Expected is computed per AWB. Anything above the ${inr(
      TOL
    )} tolerance is flagged as an overcharge error and aggregated by courier code.`,
  ],
  [
    '4',
    'Team raises a structured dispute with the courier',
    'Flagged AWBs are bundled into a dispute pack (AWB list, expected vs billed, total recoverable) and sent to the courier’s Weight/Finance POC for credit.',
  ],
]

export default function Overcharge() {
  const toast = useToast()
  const [showReport, setShowReport] = useState(false)
  const [note, setNote] = useState('')

  /* module is scoped to the D2C vertical — only D2C demo rows surface */
  const rows = DEMO_ROWS.filter((r) => r.vertical === 'D2C')

  const runDemo = (msg) => {
    setNote(msg || '')
    setShowReport(true)
  }

  return (
    <>
      <PageTitle>Courier Overcharge Errors</PageTitle>

      <PageHeader
        title='Courier Overcharge Errors'
        sub='Catches when a courier bills above the agreed rate card on the same shipment, and lets the team raise a structured dispute.'
        actions={
          <span className='banner' style={{margin: 0}}>
            <BoltOutlinedIcon style={{width: 13, height: 13}} />
            Preview · Coming soon
          </span>
        }
      />

      {/* hero empty-state explaining the feature */}
      <div
        className='card card-pad'
        style={{
          marginBottom: 16,
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: 'var(--warning-bg)',
            color: 'var(--warning)',
          }}
        >
          <AssessmentOutlinedIcon style={{width: 30, height: 30}} />
        </div>
        <div style={{flex: 1, minWidth: 280}}>
          <h2 style={{margin: '2px 0 8px'}}>
            Detect courier overcharges automatically
          </h2>
          <p className='muted' style={{fontSize: 13.5, lineHeight: 1.7, maxWidth: 720}}>
            This module will detect when a courier partner has billed Prozo{' '}
            <b>more than the agreed rate card</b> for the same shipment. Each AWB on the courier
            invoice is matched to the governing ProShip rate card for that courier code, zone and
            weight slab. Any variance above <b>{inr(TOL)}</b> is flagged as an overcharge error and
            can be packaged into a structured dispute back to the courier.
          </p>
          <div className='row gap8 wrap' style={{marginTop: 12}}>
            <span className='chip'>
              <ReceiptOutlinedIcon style={{width: 14, height: 14}} />
              AWB-level invoice match
            </span>
            <span className='chip'>
              <ScaleOutlinedIcon style={{width: 14, height: 14}} />
              Rate-card variance engine
            </span>
            <span className='chip'>
              <WarningAmberOutlinedIcon style={{width: 14, height: 14}} />
              &gt; {inr(TOL)} flagged as error
            </span>
            <span className='chip'>
              <MailOutlineOutlinedIcon style={{width: 14, height: 14}} />
              One-click structured dispute
            </span>
          </div>
        </div>
      </div>

      {/* under-development banner */}
      <div className='warnbox info' style={{marginBottom: 16}}>
        <BuildOutlinedIcon style={{width: 18, height: 18}} />
        <div>
          This feature is <b>coming soon</b>. The preview below uses sample data so you can see the
          intended output — nothing is saved.
        </div>
      </div>

      {/* "How it will work" numbered list as a timeline */}
      <div className='card card-pad' style={{marginBottom: 16}}>
        <div className='seclabel' style={{marginBottom: 12}}>
          How it will work
        </div>
        <div className='timeline'>
          {HOW_STEPS.map(([n, title, desc]) => (
            <div className='tl-item green' key={n}>
              <div className='tl-card'>
                <div className='row center gap8'>
                  <span
                    className='avatar'
                    style={{width: 24, height: 24, fontSize: 12, background: 'var(--primary)'}}
                  >
                    {n}
                  </span>
                  <b>{title}</b>
                </div>
                <div style={{fontSize: 13, marginTop: 6, color: 'var(--ink-2)', lineHeight: 1.6}}>
                  {desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* DEMO preview */}
      <div className='card' style={{marginBottom: 16}}>
        <div className='card-hd'>
          <h3 style={{display: 'flex', alignItems: 'center', gap: 6}}>
            <VisibilityOutlinedIcon style={{width: 16, height: 16}} />
            Demo preview
          </h3>
          <span className='muted' style={{fontSize: 12}}>
            Interactive preview · no data is saved
          </span>
        </div>
        <div className='card-pad'>
          <p className='muted' style={{fontSize: 13, margin: '0 0 12px', lineHeight: 1.6}}>
            Upload any AWB-level invoice (or just click below) to see how the variance report will
            look. The output table is generated from dummy rows for illustration only.
          </p>

          <FileDrop
            accept='.csv,.xlsx'
            maxMB={10}
            title='Drag & drop an AWB-level courier invoice'
            hint='Accepted: .csv, .xlsx · max 10 MB · one row per AWB. (Demo — file contents are not parsed.)'
            sampleName={TEMPLATE_NAME}
            sampleLabel='Download invoice template'
            sampleContent={TEMPLATE_CONTENT}
            onFile={() => runDemo('Mock run — matched against dummy rate cards.')}
            onError={(msg) => toast(msg, 'error')}
          />

          {/* let reviewers see the demo without needing a file */}
          <button className='btn btn-primary' style={{marginTop: 10}} onClick={() => runDemo('')}>
            <BoltOutlinedIcon style={{width: 15, height: 15}} />
            Run sample variance check
          </button>

          {note ? (
            <div
              className='hlp'
              style={{marginTop: 6, paddingLeft: 2, display: 'flex', alignItems: 'center', gap: 6}}
            >
              <InfoOutlinedIcon style={{width: 13, height: 13}} />
              {note}
            </div>
          ) : null}

          {showReport ? <VarianceReport rows={rows} toast={toast} /> : null}
        </div>
      </div>
    </>
  )
}

/* ───────── DEMO variance output ───────── */
function VarianceReport({rows, toast}) {
  if (!rows.length) {
    return (
      <div style={{marginTop: 16}}>
        <div className='empty'>
          <div className='ic'>
            <AssessmentOutlinedIcon style={{width: 30, height: 30}} />
          </div>
          <h3>No rows for your vertical</h3>
          <p>The sample invoice has no AWBs in the vertical you can view.</p>
        </div>
      </div>
    )
  }

  const errs = rows.filter(isError)
  const totalOver = errs.reduce((s, r) => s + variance(r), 0)
  const biggest = errs.reduce((m, r) => Math.max(m, variance(r)), 0)

  const kpis = [
    {
      label: 'AWBs in invoice',
      value: rows.length,
      icon: <ReceiptOutlinedIcon style={{width: 14, height: 14}} />,
      sub: 'rows matched to rate card',
    },
    {
      label: 'Overcharge errors',
      value: errs.length,
      icon: <WarningAmberOutlinedIcon style={{width: 14, height: 14}} />,
      sub: `variance > ${inr(TOL)}`,
    },
    {
      label: 'Total overcharged',
      value: inr(Math.round(totalOver)),
      icon: <AttachMoneyOutlinedIcon style={{width: 14, height: 14}} />,
      sub: 'recoverable via dispute',
      accent: '#DC2626',
    },
    {
      label: 'Largest single variance',
      value: inr(Math.round(biggest)),
      icon: <BoltOutlinedIcon style={{width: 14, height: 14}} />,
      sub: 'on one AWB',
    },
  ]

  const columns = [
    {
      key: 'awb',
      label: 'AWB',
      render: (r) => <span className='mono'>{r.awb}</span>,
    },
    {
      key: 'cpCode',
      label: 'Courier Code',
      value: (r) => r.cpCode,
      render: (r) => (
        <div>
          <b>{r.courier}</b>
          <div className='mono muted' style={{fontSize: 11}}>
            {r.cpCode}
          </div>
        </div>
      ),
    },
    {
      key: 'zone',
      label: 'Zone / Slab',
      sortable: false,
      render: (r) => (
        <span>
          {r.zone}{' '}
          <span className='muted' style={{fontSize: 11}}>
            · {r.slab}
          </span>
        </span>
      ),
    },
    {
      key: 'billed',
      label: 'Billed',
      align: 'right',
      render: (r) => <b>{inr(r.billed)}</b>,
    },
    {
      key: 'expected',
      label: 'Rate-card expected',
      align: 'right',
      value: (r) => r.expected,
      render: (r) => inr(r.expected),
    },
    {
      key: 'variance',
      label: 'Variance',
      align: 'right',
      value: (r) => variance(r),
      exportValue: (r) => variance(r),
      render: (r) => {
        const v = variance(r)
        const col = v > TOL ? 'var(--error)' : v > 0 ? 'var(--warning)' : '#90A4AE'
        const sign = v > 0 ? '+' : ''
        return (
          <b style={{color: col}}>
            {sign}
            {inr(v)}
          </b>
        )
      },
    },
    {
      key: 'flag',
      label: 'Flag',
      sortable: false,
      value: (r) => (isError(r) ? 'error' : 'ok'),
      exportValue: (r) => (isError(r) ? 'Overcharge' : 'OK'),
      render: (r) =>
        isError(r) ? (
          <Badge status='mismatch' label='Overcharge' />
        ) : (
          <Badge status='match' label={`Within ${inr(TOL)}`} />
        ),
    },
  ]

  const filters = [
    {
      key: 'flag',
      label: 'Result',
      type: 'multi',
      options: [
        {value: 'error', label: 'Overcharge error'},
        {value: 'ok', label: 'Within tolerance'},
      ],
      predicate: (r, sel) => sel.has(isError(r) ? 'error' : 'ok'),
    },
  ]

  const rowActions = (r) =>
    [
      {
        label: 'View on rate card',
        icon: <AttachMoneyOutlinedIcon fontSize='small' />,
        onClick: () => toast(`Opening the ${r.vertical} rate card for ${r.cpCode}.`, 'info'),
      },
      isError(r)
        ? {
            label: 'Add to dispute (preview)',
            icon: <MailOutlineOutlinedIcon fontSize='small' />,
            onClick: () =>
              toast(
                `AWB ${r.awb} would be added to the courier dispute pack. Coming soon.`,
                'info'
              ),
          }
        : null,
    ].filter(Boolean)

  return (
    <div style={{marginTop: 16}}>
      <div
        className='seclabel'
        style={{marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8}}
      >
        Sample variance report <Badge status='placeholder' label='Illustrative' />
      </div>

      <KpiStrip cards={kpis} />

      <div>
        <SupplyTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.awb}
          searchKeys={['awb', 'cpCode', 'courier', 'zone']}
          searchPlaceholder='Search AWB or courier code...'
          defaultSort={{key: 'variance', dir: 'desc'}}
          filters={filters}
          rowActions={rowActions}
          exportName='overcharge-variance-demo'
          emptyText='No AWBs in this sample.'
        />
      </div>

      <div className='warnbox err' style={{marginTop: 16}}>
        <ReportOutlinedIcon style={{width: 18, height: 18}} />
        <div>
          <b>
            {errs.length} overcharge error{errs.length === 1 ? '' : 's'}
          </b>{' '}
          totalling <b>{inr(Math.round(totalOver))}</b> would be raised as a structured dispute.
          (Preview — the dispute flow is coming soon.)
        </div>
      </div>
    </div>
  )
}
